import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Check, RefreshCw } from 'lucide-react'
import { Button as ButtonComponent } from '@/components/ui/button'
import HelperDialog, { HelperDialogLoading } from '@/components/campaigns/HelperDialog'
import LicenseOverviewForm, { LicenseOverview } from '@/components/campaigns/LicenseOverviewForm'
import SystemPromptEditor, {
    CustomSystemPromptBadge,
    EditSystemPromptButton,
} from '@/components/campaigns/SystemPromptEditor'
import { SUGGEST_LICENSE_TEXT } from '@/graphql/ads/prompt-suggestion-queries'
import { errorCodeOf, LicenseDraft, OVERVIEW_REQUIRED, useLicenseOverviewStep } from '@/hooks/useLicenseOverviewStep'
import {
    SystemPromptChange,
    systemPromptVariableOf,
    useSuggestionSystemPrompt,
} from '@/hooks/useSuggestionSystemPrompt'
import { ButtonProps } from '@/types'

// Type cast for the Button component from JSX file
const Button = ButtonComponent as React.FC<
    ButtonProps & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'aria-label'>
>

export type LicenseTextField = 'purpose' | 'brief'

interface SuggestLicenseTextData {
    suggestLicenseText: string[]
}

interface LicenseTextHelperProps {
    /** The saved license (Edit License) */
    campaignId?: string | null
    /** The license that is not saved yet (Add License); the helper is disabled without this or campaignId */
    draft?: LicenseDraft
    field: LicenseTextField
    /** Text currently in the form field, saved or not; sent as the draft to improve */
    value: string
    onSelect: (text: string) => void
    disabled?: boolean
    className?: string
}

const COPY: Record<LicenseTextField, { name: 'PURPOSE' | 'BRIEF'; label: string; trigger: string; submit: string }> = {
    purpose: {
        name: 'PURPOSE',
        label: 'License Purpose',
        trigger: 'Help me with purpose',
        submit: 'Suggest purpose',
    },
    brief: {
        name: 'BRIEF',
        label: 'License Short Brief',
        trigger: 'Help me with short brief',
        submit: 'Suggest short brief',
    },
}

/**
 * "Help me with purpose / short brief" for Add and Edit License, the text counterpart of KeywordHelper: opens a dialog
 * with the license overview form prefilled with the saved or typed data, then asks the backend for texts grounded in
 * it, improving the text already in the field. Using a suggestion replaces the field text and closes the dialog;
 * "Edit system prompt" shows the instructions the texts are written with, to edit and regenerate. Closing the dialog
 * ends the flow, so the next click starts again from the form.
 */
const LicenseTextHelper = ({
    campaignId,
    draft,
    field,
    value,
    onSelect,
    disabled = false,
    className,
}: LicenseTextHelperProps) => {
    const {
        overview,
        formOpen,
        setFormOpen,
        loading: overviewLoading,
        open: openOverview,
        saved: overviewSaved,
        targetOf,
        available,
    } = useLicenseOverviewStep(campaignId, draft)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [suggestions, setSuggestions] = useState<string[]>([])
    const [failed, setFailed] = useState(false)
    const [empty, setEmpty] = useState(false)
    const [suggest, { loading }] = useMutation<SuggestLicenseTextData>(SUGGEST_LICENSE_TEXT)
    const copy = COPY[field]
    const systemPrompt = useSuggestionSystemPrompt({ campaignId, kind: copy.name, active: dialogOpen })
    const draftText = value.trim() || null

    const requestSuggestions = async (
        current: LicenseOverview | null = overview,
        override: string | null = systemPrompt.override,
    ) => {
        setFailed(false)
        setEmpty(false)
        try {
            const { data } = await suggest({
                variables: {
                    ...targetOf(current),
                    field: copy.name,
                    draft: draftText,
                    ...systemPromptVariableOf(override),
                },
            })
            const received = data?.suggestLicenseText ?? []
            setSuggestions(received)
            setEmpty(received.length === 0)
        } catch (error) {
            setSuggestions([])
            if (errorCodeOf(error) === OVERVIEW_REQUIRED) setFormOpen(true)
            else setFailed(true)
        }
    }

    const handleHelp = async () => {
        setDialogOpen(true)
        setFailed(false)
        setEmpty(false)
        setSuggestions([])
        if (!(await openOverview())) setFailed(true)
    }

    /** Closes the dialog and clears the flow, so the next click starts again from the overview form. */
    const close = () => {
        setDialogOpen(false)
        setFormOpen(false)
        setSuggestions([])
        setFailed(false)
        setEmpty(false)
        systemPrompt.reset()
    }

    const handleOverviewSaved = (saved: LicenseOverview) => {
        overviewSaved(saved)
        requestSuggestions(saved)
    }

    const handleSystemPromptApplied = (change: SystemPromptChange) =>
        requestSuggestions(overview, systemPrompt.apply(change))

    const handleUse = (text: string) => {
        onSelect(text)
        close()
    }

    const busy = loading || overviewLoading

    return (
        <div className={className}>
            <Button
                type="button"
                size="sm"
                className="tw:!mt-1"
                onClick={handleHelp}
                disabled={disabled || busy || !available}
            >
                {copy.trigger}
            </Button>

            <HelperDialog open={dialogOpen} onOpenChange={(open) => !open && close()} title={copy.trigger}>
                {overviewLoading ? (
                    <HelperDialogLoading label="Loading the license overview…" />
                ) : formOpen && available ? (
                    <LicenseOverviewForm
                        bordered={false}
                        campaignId={campaignId ?? undefined}
                        initialValues={overview}
                        submitLabel={copy.submit}
                        onSaved={handleOverviewSaved}
                        onCancel={close}
                    />
                ) : systemPrompt.editing ? (
                    <SystemPromptEditor
                        kind={copy.name}
                        target={targetOf()}
                        draft={draftText}
                        override={systemPrompt.override}
                        onCancel={systemPrompt.cancel}
                        onApplied={handleSystemPromptApplied}
                    />
                ) : loading ? (
                    <HelperDialogLoading label="Generating suggestions…" />
                ) : (
                    <>
                        {failed && (
                            <p role="alert" className="tw:text-sm tw:text-red-400 tw:m-0">
                                We could not suggest a text right now. Please try again.
                            </p>
                        )}

                        {empty && (
                            <p className="tw:text-sm tw:text-gray-400 tw:m-0">
                                There are no suggestions right now. Please try again.
                            </p>
                        )}

                        {(failed || empty) && systemPrompt.custom && (
                            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:mt-2">
                                <CustomSystemPromptBadge />
                                <EditSystemPromptButton onClick={systemPrompt.edit} />
                            </div>
                        )}

                        {suggestions.length > 0 && (
                            <div>
                                <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2 tw:mb-2">
                                    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                                        <span className="tw:text-sm tw:font-semibold">Pick a text to use</span>
                                        {systemPrompt.custom && <CustomSystemPromptBadge />}
                                    </div>
                                    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                setSuggestions([])
                                                setFormOpen(true)
                                            }}
                                        >
                                            Edit overview
                                        </Button>
                                        <EditSystemPromptButton onClick={systemPrompt.edit} />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => requestSuggestions()}
                                            disabled={loading}
                                        >
                                            <RefreshCw size={14} />
                                            More ideas
                                        </Button>
                                    </div>
                                </div>

                                {(overview?.primaryUrl || Boolean(overview?.socialUrls?.length)) &&
                                    overview?.brandProfileStatus === 'failed' && (
                                        <p className="tw:text-xs tw:text-gray-400 tw:mt-0 tw:mb-2">
                                            We could not read the link, so these texts use the rest of the license
                                            details.
                                        </p>
                                    )}

                                <ul
                                    aria-label={`${copy.label} suggestions`}
                                    className="tw:flex tw:flex-col tw:gap-2 tw:list-none tw:!p-0 tw:m-0"
                                >
                                    {suggestions.map((text, index) => (
                                        <li
                                            key={text}
                                            className="tw:flex tw:items-start tw:gap-3 tw:rounded-md tw:border tw:border-gray-600 tw:p-2"
                                        >
                                            <p className="tw:flex-1 tw:text-sm tw:m-0">{text}</p>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                aria-label={`Use ${copy.label} suggestion ${index + 1}`}
                                                onClick={() => handleUse(text)}
                                            >
                                                <Check size={14} />
                                                Use
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </HelperDialog>
        </div>
    )
}

export default LicenseTextHelper
